import { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import {
  X, Calendar as CalendarIcon, Link2, AlertTriangle, RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  AccordionRoot,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Button,
  IconButton,
  Checkbox,
  Modal,
  DatePicker,
  TextField,
} from '@anyemp/ui-kit';
import { playbooks as playbooksApi } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { Loader } from '../UI/LoadingSpinner';
import ConfirmDialog from '../UI/ConfirmDialog';
import StartPlaybookModal from './StartPlaybookModal';

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const isPastDate = (value) => {
  const d = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const toISODate = (date) => {
  if (!date) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 10);
};

const StatusBadge = ({ status, isOverdue }) => {
  if (isOverdue) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
        <AlertTriangle className="h-3 w-3" /> Overdue
      </span>
    );
  }
  const styles = {
    active:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled: 'bg-light-bg-tertiary text-light-text-secondary dark:bg-dark-bg-tertiary dark:text-dark-text-secondary',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
      {status}
    </span>
  );
};

const ProgressBar = ({ percent }) => (
  <div className="w-full h-1.5 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border-primary dark:border-dark-border-primary rounded-full overflow-hidden">
    <div
      className="h-full bg-primary-600 dark:bg-primary-500 transition-all duration-300"
      style={{ width: `${Math.max(0, Math.min(100, percent || 0))}%` }}
    />
  </div>
);

const StepRow = ({ step, canComplete, onToggle }) => {
  const links = step.links || [];
  const overdue = !step.is_completed && isPastDate(step.due_date);

  return (
    <div className={`flex items-start gap-3 py-2 px-2 rounded-md ${step.is_completed ? 'opacity-60' : ''} hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary`}>
      <div className="mt-0.5 flex-shrink-0">
        <Checkbox
          checked={!!step.is_completed}
          isDisabled={!canComplete}
          onChange={() => onToggle(step)}
          size="small"
          color="primary"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium ${step.is_completed ? 'line-through text-light-text-tertiary dark:text-dark-text-tertiary' : 'text-light-text-primary dark:text-dark-text-primary'}`}>
            {step.title}
          </span>
          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{formatDate(step.due_date)}</span>
          {overdue && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <AlertTriangle className="h-3 w-3" /> overdue
            </span>
          )}
          {!step.is_required && (
            <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">(optional)</span>
          )}
        </div>
        {step.description && (
          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5">{step.description}</p>
        )}
        {links.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {links.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                <Link2 className="h-3 w-3" />{link.title || 'Link'}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ExtendModal = ({ open, instance, onClose, onConfirm }) => {
  const [endsAt, setEndsAt] = useState(toDate(instance.ends_at));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!endsAt) return;
    setSaving(true);
    try {
      await onConfirm(toISODate(endsAt));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small" showCloseButton>
      <div className="space-y-4">
        <h3 className="text-base font-semibold text-fg-primary pr-7">Extend stage</h3>
        <DatePicker
          label="New end date"
          mode="single"
          value={endsAt}
          onChange={setEndsAt}
        />
        <div className="flex justify-end gap-2">
          <Button variant="outlined" color="neutral" onClick={onClose}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} disabled={!endsAt || saving}>
            {saving ? 'Saving...' : 'Extend'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const InstanceCard = ({ instance, canCancel, canExtend, canComplete, onToggleStep, onCancel, onExtend }) => {
  const [showExtend, setShowExtend] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const steps = instance.step_instances || [];
  const isActive = instance.status === 'active';
  const itemId = `playbook-${instance.id}`;

  const showExtendBtn = isActive && canExtend;
  const showCancelBtn = isActive && canCancel;
  const headerActions = showExtendBtn || showCancelBtn ? (
    <div className="flex items-center gap-1">
      {showExtendBtn && (
        <IconButton
          aria-label="Extend stage"
          icon={<RotateCcw className="h-3.5 w-3.5" />}
          size="small"
          variant="ghost"
          color="primary"
          onClick={() => setShowExtend(true)}
          title="Extend"
        />
      )}
      {showCancelBtn && (
        <IconButton
          aria-label="Cancel stage"
          icon={<X className="h-3.5 w-3.5" />}
          size="small"
          variant="ghost"
          color="error"
          onClick={() => setConfirmCancel(true)}
          title="Cancel"
        />
      )}
    </div>
  ) : null;

  return (
    <div className={`border rounded-lg overflow-hidden ${instance.is_overdue ? 'border-red-300 dark:border-red-700 bg-red-50/20 dark:bg-red-900/5' : 'border-light-border-primary dark:border-dark-border-primary'}`}>
      <AccordionRoot
        variant="ghost"
        size="small"
        defaultOpenIds={isActive ? [itemId] : []}
        divider={false}
      >
        <AccordionItem id={itemId}>
          <AccordionTrigger endAdornment={headerActions}>
            <div className="flex-1 text-left min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary">
                  {instance.playbook?.name || 'Playbook'}
                </span>
                <StatusBadge status={instance.status} isOverdue={instance.is_overdue} />
                <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                  {formatDate(instance.started_at)} – {formatDate(instance.ends_at)}
                </span>
              </div>
              {isActive && (
                <div className="mt-1.5 flex items-center gap-2">
                  <ProgressBar percent={instance.progress_percent} />
                  <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary w-10 text-right">{instance.progress_percent}%</span>
                </div>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {steps.length === 0 ? (
              <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary p-3">No steps</p>
            ) : (
              steps.map((step) => (
                <StepRow key={step.id} step={step} canComplete={canComplete && isActive} onToggle={onToggleStep} />
              ))
            )}
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>

      {showExtend && (
        <ExtendModal
          open={showExtend}
          instance={instance}
          onClose={() => setShowExtend(false)}
          onConfirm={(endsAt) => onExtend(instance, endsAt)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmCancel}
        onClose={() => { setConfirmCancel(false); setCancelReason(''); }}
        onConfirm={() => onCancel(instance, cancelReason).then(() => { setConfirmCancel(false); setCancelReason(''); })}
        title={`Cancel "${instance.playbook?.name}"?`}
        confirmText="Cancel stage"
        variant="danger"
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Reason (optional)"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </ConfirmDialog>
    </div>
  );
};

const StagesTab = forwardRef(({ talentId, talentType, canStart }, ref) => {
  const { hasPermission } = useAuth();

  const [instances, setInstances] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStart, setShowStart] = useState(false);

  // Granular runtime permissions — mirror backend: routes/playbookInstances.js & playbookStepInstances.js.
  const canCancel   = useMemo(() => hasPermission('talent-service', 'playbook_instance', 'cancel'),        [hasPermission]);
  const canExtend   = useMemo(() => hasPermission('talent-service', 'playbook_instance', 'extend'),        [hasPermission]);
  const canComplete = useMemo(() => hasPermission('talent-service', 'playbook_instance', 'complete-step'), [hasPermission]);

  const load = async () => {
    setLoading(true);
    try {
      const [instancesRes, playbooksRes] = await Promise.all([
        playbooksApi.listForTalent(talentId),
        playbooksApi.list({ isActive: true, talentType }),
      ]);
      setInstances(instancesRes.data || []);
      setPlaybooks(playbooksRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load stages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [talentId]);

  useImperativeHandle(ref, () => ({ reload: load, openStart: () => setShowStart(true) }), [talentId]);

  const handleToggle = async (step) => {
    try {
      if (step.is_completed) {
        await playbooksApi.uncompleteStep(step.id);
      } else {
        await playbooksApi.completeStep(step.id);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update step');
    }
  };

  const handleCancel = async (instance, reason) => {
    try {
      await playbooksApi.cancelInstance(instance.id, reason);
      toast.success('Stage cancelled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel');
      throw err;
    }
  };

  const handleExtend = async (instance, endsAt) => {
    try {
      await playbooksApi.extendInstance(instance.id, endsAt);
      toast.success('Stage extended');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to extend');
    }
  };

  const handleStart = async ({ playbook_id, started_at, ends_at }) => {
    try {
      await playbooksApi.start(talentId, { playbook_id, started_at, ends_at });
      toast.success('Playbook started');
      setShowStart(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start playbook');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader size="md" /></div>;
  }

  const activeInstances = instances.filter((i) => i.status === 'active');
  const historicInstances = instances.filter((i) => i.status !== 'active');

  return (
    <div className="space-y-4">
      <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
        {activeInstances.length} active · {historicInstances.length} completed
      </p>

      {instances.length === 0 ? (
        <div className="text-center py-12 text-light-text-tertiary dark:text-dark-text-tertiary">
          <CalendarIcon className="mx-auto h-10 w-10 opacity-40" />
          <p className="mt-2 text-sm">No stages yet</p>
          {canStart && playbooks.length > 0 && (
            <p className="mt-1 text-xs">Click "Start Playbook" to add the first one</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {activeInstances.map((inst) => (
            <InstanceCard
              key={inst.id} instance={inst}
              canCancel={canCancel} canExtend={canExtend} canComplete={canComplete}
              onToggleStep={handleToggle} onCancel={handleCancel} onExtend={handleExtend}
            />
          ))}
          {historicInstances.length > 0 && (
            <>
              <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary pt-3 pb-1 uppercase tracking-wider">History</div>
              {historicInstances.map((inst) => (
                <InstanceCard
                  key={inst.id} instance={inst}
                  canCancel={false} canExtend={false} canComplete={false}
                  onToggleStep={handleToggle} onCancel={handleCancel} onExtend={handleExtend}
                />
              ))}
            </>
          )}
        </div>
      )}

      {showStart && (
        <StartPlaybookModal
          playbooks={playbooks}
          existingActive={activeInstances}
          onClose={() => setShowStart(false)}
          onConfirm={handleStart}
        />
      )}
    </div>
  );
});

StagesTab.displayName = 'StagesTab';

export default StagesTab;
